import type { Locale } from '@tuic/i18n';
import type { Port } from '../../generated/pcl';
import { CacheTag } from '../../utils';
import { slugify } from '@tuic/lib/src/helpers/slug';
import { remember, serializeDataToCacheKey } from '@tuic/cache';
import { Pcl } from '../../index';
import { logger } from '@tuic/logger';

export type ApiKitPort = Port & {
  slug: string | undefined;
};

/**
 * Fetches the port details for given portCode.
 */
export const getPortDetails = async (
  portCode: string,
  locale: Locale,
): Promise<ApiKitPort | null> => {
  let { data, error } = await Pcl.getPortsByPortCode({
    path: { portCode },
    query: { locale },
  });

  if (error) {
    logger.error(
      `Could not fetch port details. Port: ${portCode}, Locale: ${locale}`,
    );
    logger.error(error);

    return null;
  }

  if (typeof data === 'undefined') {
    return null;
  }

  let port = data;

  return {
    ...port,
    slug:
      port.name && port.country?.name
        ? slugify(`${port.name}-${port.country?.name}`)
        : undefined,
  };
};

/**
 * Fetches the port details for given portCode.
 */
export const cachedGetPortDetails = (
  portCode: string,
  locale: Locale,
): Promise<ApiKitPort | null> =>
  remember(
    serializeDataToCacheKey([CacheTag.pcl, 'getPortDetails', locale, portCode]),
    () => getPortDetails(portCode, locale),
    {
      tags: [CacheTag.pcl, CacheTag.portResource, CacheTag.getPort],
      ttl: 600,
    },
  );
